import i18n from "@/i18n";
import huLocales from "@/locales/hu.json";
import { fallbackLocale } from "@/constants/config";
import { Base64 } from "js-base64";

let missingTranslations = {};
let untranslatedText = [];
let globalValueHolder = {};

export default class Util {
  static LAST_SUCCESSFUL_LOGIN_EMAIL_COOKIE = "lastSuccessfulLoginEmail";
  static LAST_SUCCESSFUL_LOGIN_EMAIL_MAX_AGE = 60 * 60 * 24 * 30 * 6;
  static MARKETING_ATTRIBUTION_COOKIE_MAX_AGE = 60 * 60 * 24 * 30 * 6;
  static MARKETING_ATTRIBUTION_KEYS = [
    "utm_campaign",
    "utm_source",
    "utm_medium",
    "utm_term",
    "utm_content",
    "gclid",
  ];

  static setCookie(name, value, maxAge) {
    document.cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}; max-age=${maxAge}; path=/; SameSite=Lax`;
  }

  static getCookie(name) {
    const encodedName = `${encodeURIComponent(name)}=`;
    const cookies = document.cookie ? document.cookie.split("; ") : [];
    const cookie = cookies.find((item) => item.indexOf(encodedName) === 0);

    return cookie ? decodeURIComponent(cookie.substring(encodedName.length)) : "";
  }

  static storeLastSuccessfulLoginEmail(email) {
    this.setCookie(
      this.LAST_SUCCESSFUL_LOGIN_EMAIL_COOKIE,
      email,
      this.LAST_SUCCESSFUL_LOGIN_EMAIL_MAX_AGE
    );
  }

  static getLastSuccessfulLoginEmail() {
    return this.getCookie(this.LAST_SUCCESSFUL_LOGIN_EMAIL_COOKIE);
  }

  static storeMarketingAttributionFromUrl(search = window.location.search) {
    const urlParams = new URLSearchParams(search);

    this.MARKETING_ATTRIBUTION_KEYS.forEach((key) => {
      const value = urlParams.get(key);

      if (value) {
        this.setCookie(key, value, this.MARKETING_ATTRIBUTION_COOKIE_MAX_AGE);
      }
    });
  }

  static getMarketingAttributionMetaTags() {
    return this.MARKETING_ATTRIBUTION_KEYS.reduce((metaTags, key) => {
      const value = this.getCookie(key);

      if (value) {
        metaTags[key] = value;
      }

      return metaTags;
    }, {});
  }

  static storeGlobalData(key, data) {
    globalValueHolder[key] = data;
  }

  static getGlobalData(key) {
    return globalValueHolder[key];
  }

  static doesJwtHasBoRoleOrOriginalUserId() {
    const token = localStorage.getItem("token");
    if (!token) {
      return false;
    }

    try {
      const tokenParts = token.split('.');
      if (tokenParts.length !== 3) {
        throw new Error('Token does not appear to be a JWT');
      }

      const payload = tokenParts[1];
      const decodedPayload = Base64.decode(payload);
      const payloadObj = JSON.parse(decodedPayload);

      if (payloadObj.backOfficeRole || payloadObj.originalUserId) {
        return true;
      }

      return false;
    } catch (err) {
      console.error(err);
      return false;
    }
  }

  static hasPartCurrentJwt(part) {
    const token = localStorage.getItem("token");
    if (!token) {
      return false;
    }

    try {
      const tokenParts = token.split('.');
      if (tokenParts.length !== 3) {
        return false;
      }

      const payload = tokenParts[1];
      const decodedPayload = Base64.decode(payload);
      const payloadObj = JSON.parse(decodedPayload);

      if (!payloadObj.role) {
        return false;
      }

      return !!payloadObj[part];
    } catch (err) {
      console.error(err);
      return false;
    }
  }

  static isJson(str) {
    try {
      JSON.parse(str);
    } catch (e) {
      return false;
    }
    return true;
  }

  static removeExtension(file) {
    if (file.includes(".")) {
      return file.split(".").slice(0, -1).toString();
    } else {
      return file;
    }
  }

  static getFallbackLanguage(parsedData) {
    if (parsedData[i18n.locale]) {
      return parsedData[i18n.locale];
    } else {
      let i = 0;
      let translatedString = undefined;

      while (i < fallbackLocale.length && !translatedString) {
        translatedString = parsedData[fallbackLocale[i]];
        ++i;
      }

      return translatedString;
    }
  }

  static translationEngine(x, dataArray = []) {
    let self = this;
    let translatedMessage = null;

    if (this.isJson(x) && isNaN(x)) {
      let parsedData = JSON.parse(x);
      translatedMessage = parsedData
        ? self.getFallbackLanguage(parsedData)
        : parsedData;
    } else {
      let translatedDataArray = [];

      dataArray.forEach((item) => {
        let translatedArrayItem = self.translationEngine(item);
        translatedDataArray.push(translatedArrayItem);
      });

      translatedMessage = i18n.t(x, translatedDataArray);

      if (translatedMessage === x) {
        this.addToMissingTranslations(x, i18n.locale);
      }
    }

    return translatedMessage;
  }

  static addToMissingTranslations(translationsData, lang) {
    let parsedData = [];

    try {
      parsedData = translationsData.split(".");
    } catch {
      // console.log('hiba', translationsData);
    }

    if (parsedData.length > 1 && translationsData.indexOf(" ") < 0) {
      this.addRecursiveMissingTranslations(missingTranslations, parsedData, 0);
    } else {
      let found = false;

      for (let i = 0; i < untranslatedText.length; i++) {
        if (untranslatedText[i].text === parsedData[0]) {
          found = true;
          break;
        }
      }

      if (!found) {
        untranslatedText.push({
          lang: lang,
          text: parsedData[0],
        });
      }
    }
  }

  static addRecursiveMissingTranslations(target, translationArray, index) {
    let self = this;
    if (!target[translationArray[index]]) {
      target[translationArray[index]] = {};
    }
    if (index < translationArray.length - 1) {
      self.addRecursiveMissingTranslations(
        target[translationArray[index]],
        translationArray,
        ++index
      );
    } else {
      target[translationArray[index]] = "";
    }
  }

  static generateRandomStr() {
    return (Math.random() + 1).toString(36).substring(7);
  }

  static showValidationErrors(err, $refs) {
    if (err.isAxiosError && err.response.data.failList) {
      err.response.data.failList.forEach(validationFail => {
          /*

        if ($refs[validationFail.field]) {

          console.log($refs[validationFail.field]);
          try {
            $refs[validationFail.field].$el.classList.add('is-invalid');
            console.log("try");
          } catch {
            $refs[validationFail.field][0].$el.classList.add('is-invalid');  
            console.log("catch");
          }
        }
        */
        if ($refs[validationFail.field + '-validation-message']) {
          try{
            $refs[validationFail.field + '-validation-message'][0].innerHTML = validationFail.message;
          } catch {
            $refs[validationFail.field + '-validation-message'].innerHTML = validationFail.message;
          }
        }
      });
      return true;
    } else if (err.isAxiosError && Array.isArray(err.response.data)) {
      for (let errorItem of err.response.data) {
        if (errorItem.field) {
          if ($refs[errorItem.field + '-validation-message']) {
            try{
              $refs[errorItem.field + '-validation-message'][0].innerHTML = errorItem.errors[0].errorMessage;
            } catch {
              $refs[errorItem.field + '-validation-message'].innerHTML = errorItem.errors[0].errorMessage;
            }
          }
        }
      }
    }

    console.error(err);
    return false;
  }

  static isOnlyWarning(err) {
    try {
      return err.response.data.severity === "WARNING";
    } catch (ignore) {
      return false;
    }
  }

  static getFirstValidationMessage(err) {
    try {
      return err.response.data.failList[0].message;
    } catch (ignore) {
      return '';
    }
  }

  static clearValidationErrors($el) {
    while($el.getElementsByClassName('is-invalid').length > 0) {
      $el.getElementsByClassName('is-invalid')[0].classList.remove('is-invalid');
    }
    $el.getElementsByClassName('validation-error-msg').forEach(e => e.innerHTML = '');
  }

  static showToastUnexpectedError(vInstance) {
    vInstance.$toast.error({
      title: vInstance.$cte("toast.error"),
      message: vInstance.$cte("common.systemError"),
    });
  }
}

window.getUntranslatedText = function () {
  console.log("Untranslated text:", untranslatedText);
};

window.getMissingTranslations = function () {
  console.log("Missing translations:", missingTranslations);
};

window.clearLocaleObject = function (tempVariable) {
  if (typeof tempVariable === "object") {
    let objectKeys = Object.keys(tempVariable);

    objectKeys.forEach((key) => {
      tempVariable[key] = window.clearLocaleObject(tempVariable[key]);
    });

    return tempVariable;
  } else {
    return "";
  }
};

window.getEmptyLocaleJSON = function () {
  let emptyObject = JSON.parse(JSON.stringify(huLocales));

  emptyObject = window.clearLocaleObject(emptyObject);

  console.log(JSON.stringify(emptyObject));
};

window.copyLocaleObject = function (sourceJSON, targetJSON) {
  if (typeof sourceJSON === "object") {
    let objectKeys = Object.keys(sourceJSON);

    objectKeys.forEach((key) => {
      if (!targetJSON) {
        targetJSON = {};
      }

      targetJSON[key] = window.copyLocaleObject(
        sourceJSON[key],
        targetJSON[key]
      );
    });

    return targetJSON;
  } else {
    if (!targetJSON) {
      return "";
    } else {
      return targetJSON;
    }
  }
};

window.getLocaleJSON = function (sourceLang, targetLang) {
  const sourceJSON = require("@/locales/" + sourceLang + ".json");
  let targetJSON = require("@/locales/" + targetLang + ".json");

  let finalObject = JSON.parse(JSON.stringify(targetJSON));

  finalObject = window.copyLocaleObject(sourceJSON, finalObject);

  console.log(JSON.stringify(finalObject));
};
